import * as React from "react";

// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import {
  Icon,
  type IContextualMenuItem,
  type IContextualMenuProps,
} from "@fluentui/react";
import type {
  MenuItemProps,
  MenuItemCheckboxProps,
  MenuProps,
  MenuOpenChangeData,
} from "@fluentui/react-components";
import {
  useFixOnMenuOpenedTriggerOnSubmenuHoverEnabled,
  useTriggerOnMenuDismissedOnSubmenuClose,
} from "../../hooks/useNextComponentsEnabled";
import { AppIcon, useV9AppIconEnabled } from "@1js/acui-icons";
import { convertStyleFunctOrObjectToCSSProperties } from "@1js/acui-common";

export const useShimMenuProps = (
  props: IContextualMenuProps
): Partial<MenuProps> => {
  const [isOpen, setOpen] = React.useState(false);
  const fixOnMenuOpenedTriggerOnSubmenuHoverEnabled =
    useFixOnMenuOpenedTriggerOnSubmenuHoverEnabled();

  const triggerOnMenuDismissedOnSubmenuClose =
    useTriggerOnMenuDismissedOnSubmenuClose();

  const onMenuOpenChange = (
    e:
      | MouseEvent
      | React.MouseEvent<HTMLElement, MouseEvent>
      | React.KeyboardEvent<HTMLElement>
      | TouchEvent,
    data: MenuOpenChangeData
  ) => {
    if (data.open && !(fixOnMenuOpenedTriggerOnSubmenuHoverEnabled && isOpen)) {
      setOpen(data.open);
      // Populate the menu items of submenu using this callback when the submenu is opened.
      props?.onMenuOpened?.();
      return;
    }

    // if Tab key is pressed, don't close the menu. Passing open as true to keep menu open otherwise pass data.open value
    if (e && (e as React.KeyboardEvent).key === "Tab") {
      setOpen(true);
      return;
    }

    if (!data.open && isOpen) {
      if (triggerOnMenuDismissedOnSubmenuClose) {
        // Call this callback whenever a submenu is closed.
        props?.onMenuDismissed?.();
      }

      setOpen(data.open);
    }
  };

  return {
    hasIcons: props?.items[0]?.sectionProps?.items.some(
      (i) => i.iconProps !== undefined
    ),
    onOpenChange: onMenuOpenChange,
    positioning: {
      autoSize: true,
    },
    open: isOpen,
  };
};

export const shimMenuItemProps = (
  props: IContextualMenuItem
): MenuItemProps => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const isV9IconEnabled = useV9AppIconEnabled();
  const icon = props.iconProps ? (
    isV9IconEnabled ? (
      <AppIcon
        {...props.iconProps}
        style={convertStyleFunctOrObjectToCSSProperties(
          props.iconProps?.styles
        )}
      />
    ) : (
      <Icon {...props.iconProps} />
    )
  ) : undefined;

  return {
    ...props,
    "aria-label": props.ariaLabel,
    children: props.text || props.name,
    icon,
    secondaryContent: props.secondaryText,
    persistOnClick: props.data?.persistOnClick === false,
  } as MenuItemProps;
};

export const shimMenuItemCheckboxProps = (
  props: IContextualMenuItem
): MenuItemCheckboxProps => {
  return {
    ...shimMenuItemProps(props),
    name: props.text || "name",
    value: props.value || "value",
  };
};

export const shimMenuHeaderProps = (
  props: IContextualMenuItem
): string | undefined => {
  const menuHeaderText = props.sectionProps?.title;
  if (typeof menuHeaderText === "string") {
    return menuHeaderText;
  }
  return menuHeaderText?.text;
};

export const shimMenuTitleProps = (
  props: IContextualMenuItem
): string | undefined => {
  const menuHeaderText = props.text || props.name;
  if (typeof menuHeaderText === "string") {
    return menuHeaderText;
  }
  return props.key;
};
